import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
  Backdrop,
  AppBar,
  Toolbar,
  Divider,
} from "@mui/material";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Add } from "@mui/icons-material";
import { useUser } from "../../../Services/ContexteUser";

/* --- TabPanel helper (même pattern que ManageBets) --- */
function TabPanel({ index, value, children }) {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ width: "100%" }}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ManageMiniBet = () => {
  const navigate = useNavigate();
  const { idBet } = useParams();

  // ==== Données / états d'édition (inchangés fonctionnellement) ====
  const [miniBets, setMiniBets] = useState(null);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [sportCategory, setSportCategory] = useState("");
  const [odds, setOdds] = useState([{ label: "", value: "" }]);
  const [bettingStatus, setBettingStatus] = useState("en cours");
  const [distributeWinningDone, setDistributeWinningDone] = useState();
  const [winningOdd, setWinningOdd] = useState(null);
  const { updateMonnaie } = useUser();

  // ==== UX state ====
  const [activeTab, setActiveTab] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  // Loaders
  const [loadingFetch, setLoadingFetch] = useState(true);
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const [saving, setSaving] = useState(false);
  const [distributingLoading, setDistributingLoading] = useState(false);

  // Dialog sélection de la cote gagnante
  const [openDialog, setOpenDialog] = useState(false);

  // Toast
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const idOrganisation = localStorage.getItem("idOrganisation");

  // ==== Fetch/Refresh (mêmes endpoints, format identique) ====
  const fetchBet = useCallback(
    async (mode = "fetch") => {
      const path = `organisations/${idOrganisation}/miniBets`;
      try {
        mode === "refresh" ? setLoadingRefresh(true) : setLoadingFetch(true);
        const response = await fetch(
          `https://backend-betkiff.vercel.app/bets/getBetByID/${idBet}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path }),
          }
        );
        if (!response.ok) {
          throw new Error(
            `Erreur : ${response.status} - ${response.statusText}`
          );
        }
        const data = await response.json();
        setMiniBets(data);
        setTitle(data.title || "");
        setDate(data.date || "");
        setSportCategory(data.sportCategory || "");
        setOdds(
          data.odds && data.odds.length ? data.odds : [{ label: "", value: "" }]
        );
        setDistributeWinningDone(data.distributeWinningDone);
        setBettingStatus(data.bettingStatus || "en cours");
        setIsDirty(false);
      } catch (error) {
        console.error("Erreur lors de la récupération du mini pari :", error);
        setToast({
          open: true,
          message: "Impossible de rafraîchir les données.",
          severity: "error",
        });
      } finally {
        mode === "refresh" ? setLoadingRefresh(false) : setLoadingFetch(false);
      }
    },
    [idBet, idOrganisation]
  );

  useEffect(() => {
    fetchBet("fetch");
  }, [fetchBet]);

  // ==== Handlers UI ====
  const handleTabChange = (_e, v) => setActiveTab(v);

  const handleAddOdd = () => {
    setOdds((prev) => [...prev, { label: "", value: "" }]);
    setIsDirty(true);
  };

  const handleRemoveOdd = (index) => {
    setOdds((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleOddChange = (index, key, value) => {
    setOdds((prev) =>
      prev.map((odd, i) => (i === index ? { ...odd, [key]: value } : odd))
    );
    setIsDirty(true);
  };

  // ==== Validation (cohérent avec ManageBets : simple, claire) ====
  const validation = useMemo(() => {
    const oddsOk =
      Array.isArray(odds) &&
      odds.length > 0 &&
      odds.every(
        (o) =>
          typeof o?.label === "string" &&
          o.label.trim().length > 0 &&
          Number.isFinite(Number(o?.value)) &&
          Number(o.value) > 1
      );

    const titleOk = title.trim().length > 0;
    const dateOk = date && !Number.isNaN(new Date(date).getTime());

    return { oddsOk, titleOk, dateOk };
  }, [odds, title, date]);

  // ==== Fonctions BACKEND (inchangées côté appels) + enrobage UX ====

  // Update
  const handleUpdateMiniBet = async () => {
    const updatedMiniBet = {
      date,
      odds,
      sportCategory,
      title,
      bettingStatus,
      distributeWinningDone,
      idOrganisation,
    };
    try {
      setSaving(true);
      const response = await fetch(
        `https://backend-betkiff.vercel.app/bets/updateMiniBets/${idBet}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedMiniBet),
        }
      );
      if (response.ok) {
        setToast({
          open: true,
          message: "Mini pari enregistré avec succès.",
          severity: "success",
        });
        setIsDirty(false);
        await fetchBet("refresh");
      } else {
        throw new Error("Erreur lors de la mise à jour du mini pari");
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du mini pari :", error);
      setToast({
        open: true,
        message: "Une erreur est survenue lors de l'enregistrement.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // (identique sur appels) Mise à jour du solde user
  const updateUserBalance = async (idUser, winning) => {
    const uid = idUser;
    try {
      const response = await fetch(
        `https://backend-betkiff.vercel.app/users/ById/${uid}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idOrganisation }),
        }
      );
      if (!response.ok) {
        throw new Error(
          `Erreur lors de la récupération de l'utilisateur : ${response.status}`
        );
      }
      const userData = await response.json();
      const newMoney = userData.nbMonnaie + winning;

      const updateResponse = await fetch(
        "https://backend-betkiff.vercel.app/users/updateMonnaie",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: idUser, newMoney, idOrganisation }),
        }
      );

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        console.error("Erreur API mise à jour monnaie :", errorData);
        throw new Error(
          errorData.error || "Erreur lors de la mise à jour de l'argent."
        );
      }
      updateMonnaie(newMoney);
      await updateResponse.json();
    } catch (error) {
      console.error("Erreur dans updateUserBalance :", error.message);
    }
  };

  // (identique sur appels) Calcul des gains mini-bet
  const calculateWinnings = async (bet, bettors) => {
    if (!idBet || !winningOdd) {
      setToast({
        open: true,
        message: "Veuillez sélectionner la cote gagnante.",
        severity: "warning",
      });
      return;
    }
    try {
      const response = await axios.post(
        "https://backend-betkiff.vercel.app/bets/calculateWinningMiniBet",
        {
          bet: miniBets,
          bettors: miniBets.bettors,
          idBet: idBet,
          winningOdd: winningOdd,
          idOrganisation,
        }
      );
      const { message, winningBettors } = response.data;
      setToast({ open: true, message, severity: "success" });
      return winningBettors;
    } catch (error) {
      console.error("Erreur lors du calcul des gains :", error);
      setToast({
        open: true,
        message: "Erreur lors du calcul des gains.",
        severity: "error",
      });
    }
  };

  // (identique sur appels) Distribution
  const distributeWinnings = async (bet) => {
    const { bettors } = bet;
    try {
      setDistributingLoading(true);
      const winners = await calculateWinnings(bet, bettors);
      if (winners?.winningBettors?.length > 0) {
        for (const { idUser, winnings } of winners.winningBettors) {
          await updateUserBalance(idUser, winnings);
        }
      }
      setDistributeWinningDone(true);
      const response = await axios.put(
        `https://backend-betkiff.vercel.app/bets/updateMiniBets/${idBet}`,
        {
          date,
          bettingOpen: false,
          odds,
          sportCategory,
          title,
          bettingStatus: "termine",
          distributeWinningDone: true,
          idOrganisation,
        }
      );
      console.log("Mise à jour distributeWinningDone :", response.data);
      setToast({
        open: true,
        message: "Mini pari clôturé et gains distribués.",
        severity: "success",
      });
      setOpenDialog(false);
      await fetchBet("refresh");
    } catch (error) {
      console.error("Erreur lors de la distribution des gains :", error);
      setToast({
        open: true,
        message: "Échec de la clôture.",
        severity: "error",
      });
    } finally {
      setDistributingLoading(false);
    }
  };

  // ==== Rendu ====
  if (loadingFetch || !miniBets) {
    return (
      <Box
        sx={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Chargement du mini pari…
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Statut visuel des mises
  const isClosed = bettingStatus?.toLowerCase() !== "en cours";

  return (
    <div>
      {/* ===== Header (AppBar sticky) ===== */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ background: "rgb(255, 255, 255)", backdropFilter: "blur(6px)" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            sx={{ color: "black" }}
            onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ ml: 1, flexGrow: 1, overflow: "hidden" }}>
            <Typography
              noWrap
              variant="h6"
              sx={{ fontWeight: 700, color: "black" }}>
              Mini pari — {title || "Sans titre"}
            </Typography>

            <Tooltip
              title={
                isClosed ? "Les mises sont fermées" : "Les mises sont ouvertes"
              }>
              <Chip
                label={isClosed ? "Pari fermé" : "Pari ouvert"}
                color={isClosed ? "default" : "success"}
                size="small"
                sx={{
                  color: "white",
                  bgcolor: isClosed ? "grey.700" : undefined,
                }}
              />
            </Tooltip>

            {distributeWinningDone && (
              <Tooltip title="Ce mini pari est clôturé">
                <Chip
                  label="Clôturé"
                  color="success"
                  size="small"
                  sx={{ color: "white" }}
                />
              </Tooltip>
            )}
          </Stack>

          <Tooltip title="Rafraîchir les données">
            <span>
              <IconButton
                color="inherit"
                sx={{ color: "black" }}
                onClick={() => fetchBet("refresh")}
                disabled={loadingRefresh}>
                {loadingRefresh ? (
                  <CircularProgress size={18} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>

        {loadingRefresh && <LinearProgress color="secondary" />}

        <Toolbar sx={{ padding: 0, width: "100%", overflowX: "auto" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
                color: "black",
              },
              "& .MuiTabs-indicator": { height: 3 },
            }}>
            <Tab label="1. Détails" sx={{ color: "black" }} />
            <Tab label="2. Côtes" sx={{ color: "black" }} />
          </Tabs>
        </Toolbar>
      </AppBar>

      {/* ===== Contenu ===== */}
      <Container sx={{ py: 3, pb: distributeWinningDone ? 3 : 14 }}>
        <Paper
          elevation={1}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}>
          {/* Onglet 1 : Détails */}
          <TabPanel value={activeTab} index={0}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Détails du mini pari
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Titre"
                  fullWidth
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setIsDirty(true);
                  }}
                  error={!validation.titleOk}
                  helperText={!validation.titleOk ? "Titre requis." : " "}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Date"
                  type="date"
                  fullWidth
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setIsDirty(true);
                  }}
                  InputLabelProps={{ shrink: true }}
                  error={!validation.dateOk}
                  helperText={!validation.dateOk ? "Date invalide." : " "}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Onglet 2 : Côtes */}
          <TabPanel value={activeTab} index={1}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Côtes
            </Typography>

            {/* On ajoute un padding bas pour ne jamais toucher le footer fixe */}
            <Box sx={{ pb: 3 }}>
              {odds.map((odd, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    borderColor: "divider",
                  }}>
                  {/* En-tête de carte avec action "Supprimer" bien visible */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Cote {index + 1}
                    </Typography>

                    <Tooltip title="Supprimer cette cote">
                      <span>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteForeverRoundedIcon />}
                          onClick={() => handleRemoveOdd(index)}
                          disabled={!!distributeWinningDone}>
                          Supprimer
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>

                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Titre côte"
                        fullWidth
                        value={odd.label}
                        onChange={(e) =>
                          handleOddChange(index, "label", e.target.value)
                        }
                        error={!odd.label?.trim()}
                        helperText={!odd.label?.trim() ? "Champ requis." : " "}
                        disabled={!!distributeWinningDone}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Valeur"
                        type="number"
                        fullWidth
                        value={odd.value}
                        onChange={(e) =>
                          handleOddChange(index, "value", e.target.value)
                        }
                        error={!(Number(odd.value) > 1)}
                        helperText={
                          !(Number(odd.value) > 1) ? "Doit être > 1.00." : " "
                        }
                        disabled={!!distributeWinningDone}
                        inputProps={{
                          step: 0.01,
                          min: 1.01,
                          inputMode: "decimal",
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}

              {/* Bouton d’ajout full-width + style « action secondaire » */}
              <Box sx={{ pt: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddOdd}
                  disabled={!!distributeWinningDone}
                  sx={{
                    borderStyle: "dashed",
                    py: 1.25,
                  }}>
                  Ajouter une cote
                </Button>
              </Box>
            </Box>
          </TabPanel>
        </Paper>
      </Container>

      {/* ===== Footer d’actions (fixe) ===== */}
      {!distributeWinningDone && (
        <Paper
          component="footer"
          elevation={3}
          square
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            zIndex: (t) => t.zIndex.drawer + 2,

            // Padding vertical par défaut (fallback desktop / navigateurs sans safe-area)
            py: 1.25,
            paddingBottom: 1.25,

            /* iOS 11.x (ancien Safari) */
            "@supports (padding-bottom: constant(safe-area-inset-bottom))": {
              paddingBottom: "calc(12px + constant(safe-area-inset-bottom))",
            },

            /* iOS 12+ (Safari moderne) */
            "@supports (padding-bottom: env(safe-area-inset-bottom))": {
              paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
            },
          }}>
          <Container>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              justifyContent="flex-end"
              alignItems={{ xs: "stretch", sm: "center" }}>
              {/* Enregistrer */}
              <Tooltip title="Enregistrer les modifications">
                <span>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleUpdateMiniBet}
                    disabled={
                      !isDirty ||
                      saving ||
                      !validation.titleOk ||
                      !validation.dateOk ||
                      !validation.oddsOk
                    }>
                    {saving && <CircularProgress size={18} sx={{ mr: 1 }} />}
                    Enregistrer
                  </Button>
                </span>
              </Tooltip>

              {/* Clôturer & distribuer */}
              <Tooltip title="Clôture définitive + distribution des gains">
                <span>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    disabled={distributingLoading || !validation.oddsOk}
                    onClick={() => setOpenDialog(true)}>
                    {distributingLoading && (
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                    )}
                    Clôturer & distribuer
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Container>
        </Paper>
      )}

      {/* Dialog : sélection de la cote gagnante */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm">
        <DialogTitle>Sélectionner la cote gagnante</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth>
            <InputLabel id="winningOddLabel">Cote gagnante</InputLabel>
            <Select
              labelId="winningOddLabel"
              value={winningOdd || ""}
              onChange={(e) => setWinningOdd(e.target.value)}
              label="Cote gagnante">
              {odds.map((odd, index) => (
                <MenuItem key={index} value={odd.label}>
                  {odd.label} — {odd.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="success"
            disabled={!winningOdd || distributingLoading}
            onClick={() => distributeWinnings(miniBets)}>
            {distributingLoading && (
              <CircularProgress size={18} sx={{ mr: 1 }} />
            )}
            Distribuer les gains
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop opérations longues */}
      <Backdrop
        open={distributingLoading}
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.modal + 1 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" />
          <Typography>Distribution des gains…</Typography>
        </Stack>
      </Backdrop>

      {/* Snackbar */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ManageMiniBet;
